import { CustomerViewModel, TenantViewModel, UserInputModel } from '@ail/customers-api'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../../../../hooks/useAuth'
import { useGetAllCustomersQuery, useGetTenantsQuery } from '../../../../../redux/api/customerApi'

export function AssignTenants(props: {
  editingUser: UserInputModel
  userTenant?: TenantViewModel
  onChange: (user: UserInputModel) => void
}) {
  const [assignedSearch, setAssignedSearch] = useState('')
  const [unassignedSearch, setUnassignedSearch] = useState('')
  const [expandedAssigned, setExpandedAssigned] = useState<Partial<Record<string, boolean>>>({})
  const [expandedUnassigned, setExpandedUnassigned] = useState<Partial<Record<string, boolean>>>({})
  const { t } = useTranslation('common')
  const { user } = useAuth()
  const canAssign = user?.isAdmin || (user?.isInternalUser && user?.isManager)
  const { currentData: tenants } = useGetTenantsQuery()
  const { currentData: customers } = useGetAllCustomersQuery()

  const customerMaps = useMemo(() => {
    const byId: Partial<Record<string, CustomerViewModel>> = {}
    const byTenantId: Partial<Record<string, CustomerViewModel[]>> = {}
    for (const customer of customers ?? []) {
      byId[customer.customer.id] = customer.customer
      if (!customer.customer.tenantId) continue
      const byTenantIdEntry = byTenantId[customer.customer.tenantId] ?? []
      byTenantId[customer.customer.tenantId] = byTenantIdEntry
      byTenantIdEntry.push(customer.customer)
    }
    return { byId, byTenantId }
  }, [customers])

  const assignedCustomer = useCallback(
    (c: CustomerViewModel) => {
      return (
        (c.tenantId && props.editingUser.tenants?.includes(c.tenantId)) || props.editingUser.customers?.includes(c.id)
      )
    },
    [props.editingUser.customers, props.editingUser.tenants]
  )

  const assignedFilteredCustomer = useCallback(
    (c: CustomerViewModel) => {
      return assignedCustomer(c) && (!assignedSearch || c.name.toLowerCase().includes(assignedSearch.toLowerCase()))
    },
    [assignedCustomer, assignedSearch]
  )

  const unassignedFilteredCustomer = useCallback(
    (c: CustomerViewModel) => {
      return (
        !assignedCustomer(c) && (!unassignedSearch || c.name.toLowerCase().includes(unassignedSearch.toLowerCase()))
      )
    },
    [assignedCustomer, unassignedSearch]
  )

  const assignedTenants = useMemo(() => {
    return tenants?.filter(
      (t) =>
        props.editingUser.tenants?.includes(t.id) ||
        props.editingUser.customers?.some((c) => customerMaps.byId[c]?.tenantId === t.id)
    )
  }, [customerMaps.byId, props.editingUser.customers, props.editingUser.tenants, tenants])

  const assignedFilteredTenants = useMemo(() => {
    return assignedTenants?.filter(
      (t) =>
        !assignedSearch ||
        t.name.toLowerCase().includes(assignedSearch.toLowerCase()) ||
        customerMaps.byTenantId[t.id]?.some(assignedFilteredCustomer)
    )
  }, [assignedFilteredCustomer, assignedSearch, assignedTenants, customerMaps.byTenantId])

  const unassignedTenants = useMemo(() => {
    return tenants?.filter(
      (t) =>
        t.isCompany &&
        !props.editingUser.tenants?.includes(t.id) &&
        (!customerMaps.byTenantId[t.id]?.length ||
          customerMaps.byTenantId[t.id]?.some((c) => !props.editingUser.customers?.includes(c.id)))
    )
  }, [customerMaps.byTenantId, props.editingUser.customers, props.editingUser.tenants, tenants])

  const unassignedFilteredTenants = useMemo(() => {
    return unassignedTenants?.filter(
      (t) =>
        !unassignedSearch ||
        t.name.toLowerCase().includes(unassignedSearch.toLowerCase()) ||
        customerMaps.byTenantId[t.id]?.some(unassignedFilteredCustomer)
    )
  }, [customerMaps.byTenantId, unassignedFilteredCustomer, unassignedSearch, unassignedTenants])

  const assignTenant = (tenant: TenantViewModel) => {
    const updatedUser: UserInputModel = { ...props.editingUser }
    updatedUser.tenants = [...(updatedUser.tenants ?? []), tenant.id]
    updatedUser.customers = updatedUser.customers?.filter((c) => customerMaps.byId[c]?.tenantId !== tenant.id)
    props.onChange(updatedUser)
  }

  const unassignTenant = (tenant: TenantViewModel) => {
    const updatedUser: UserInputModel = { ...props.editingUser }
    updatedUser.tenants = updatedUser.tenants?.filter((t) => t !== tenant.id)
    updatedUser.customers = updatedUser.customers?.filter((c) => customerMaps.byId[c]?.tenantId !== tenant.id)
    props.onChange(updatedUser)
  }

  const assignCustomer = (customer: CustomerViewModel) => {
    const updatedUser: UserInputModel = { ...props.editingUser }
    updatedUser.customers = [...(updatedUser.customers ?? []), customer.id]
    if (customer.tenantId) {
      const tenantCustomers = customerMaps.byTenantId[customer.tenantId]
      if (tenantCustomers?.every((c) => updatedUser.customers?.includes(c.id))) {
        updatedUser.tenants = [...(updatedUser.tenants ?? []), customer.tenantId]
        updatedUser.customers = updatedUser.customers?.filter(
          (c) => customerMaps.byId[c]?.tenantId !== customer.tenantId
        )
      }
    }
    props.onChange(updatedUser)
  }

  const unassignCustomer = (customer: CustomerViewModel) => {
    const updatedUser: UserInputModel = { ...props.editingUser }
    if (updatedUser.customers?.includes(customer.id)) {
      updatedUser.customers = updatedUser.customers.filter((c) => c !== customer.id)
    } else if (customer.tenantId && updatedUser.tenants?.includes(customer.tenantId)) {
      updatedUser.tenants = updatedUser.tenants?.filter((t) => t !== customer.tenantId)
      updatedUser.customers = customerMaps.byTenantId[customer.tenantId]
        ?.filter((c) => c.id !== customer.id)
        .map((c) => c.id)
    }
    props.onChange(updatedUser)
  }

  return (
    <div className="box">
      <div className="itemTitle">
        <span>
          <i className={'icon-groups'}></i>
          {t('TENANTS')}
        </span>
      </div>
      <div className="row">
        <div className="col-6">
          <div className="box">
            <div className="unitsTitle">
              <label>{t('ASSIGNED_TENANTS')}</label>
              <div className="input-area">
                <input
                  type="search"
                  placeholder={t('COST_CENTER_NAME_COMPANY_NAME')}
                  onChange={(e) => setAssignedSearch(e.target.value)}
                  value={assignedSearch}
                />
                {!!assignedSearch && <i className="icon-clear" onClick={() => setAssignedSearch('')} />}
                <i className="icon-search"></i>
              </div>
            </div>
            <div className="unitsListArea">
              {assignedFilteredTenants?.map((tenant) => (
                <>
                  <div className="compName">
                    <div className="custName">{tenant.name}</div>
                    <div>
                      <i
                        className={expandedAssigned[tenant.id] ? 'icon-up' : 'icon-bottom'}
                        onClick={() =>
                          setExpandedAssigned({ ...expandedAssigned, [tenant.id]: !expandedAssigned[tenant.id] })
                        }
                      ></i>
                    </div>
                    {canAssign && <i className="icon-clear" onClick={() => unassignTenant(tenant)}></i>}
                  </div>
                  {expandedAssigned[tenant.id] && (
                    <ul className={'unitsList'}>
                      {customerMaps.byTenantId[tenant.id]?.filter(assignedFilteredCustomer).map((customer) => (
                        <li>
                          {customer.name}
                          {canAssign && <i className="icon-clear" onClick={() => unassignCustomer(customer)}></i>}
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              ))}
            </div>
          </div>
        </div>
        <div className="col-6 avaUnits">
          <div className="box">
            <div className="unitsTitle">
              <label>{t('SEARCH_TENANTS')}</label>
              <div className="input-area">
                <input
                  type="search"
                  className="unit-search"
                  onChange={(e) => setUnassignedSearch(e.target.value)}
                  value={unassignedSearch}
                  placeholder={t('COST_CENTER_NAME_COMPANY_NAME')}
                />
                {!!unassignedSearch && <i className="icon-clear" onClick={() => setUnassignedSearch('')} />}
                <i className="icon-search"></i>
              </div>
            </div>
            <div className="unitsListArea">
              {unassignedFilteredTenants?.map((tenant) => (
                <>
                  <div className="compName">
                    <div className="custName">
                      {canAssign && <i className="icon-add" onClick={() => assignTenant(tenant)}></i>}
                      {tenant.name}
                    </div>
                    <div>
                      <i
                        className={expandedUnassigned[tenant.id] ? 'icon-up' : 'icon-bottom'}
                        onClick={() =>
                          setExpandedUnassigned({ ...expandedUnassigned, [tenant.id]: !expandedUnassigned[tenant.id] })
                        }
                      ></i>
                    </div>
                  </div>
                  {expandedUnassigned[tenant.id] && (
                    <ul className={'unitsList'}>
                      {customerMaps.byTenantId[tenant.id]?.filter(unassignedFilteredCustomer).map((customer) => (
                        <li>
                          {customer.name}
                          {canAssign && <i className="icon-add" onClick={() => assignCustomer(customer)}></i>}
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
